import { forwardRef, useEffect, useId, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CustomCellEditorProps } from 'ag-grid-react';
import type { ItemResponse } from '@triade/contracts';
import { useItens } from '@/api/itens';
import { useDebounced } from '@/hooks/use-debounced';
import { ctx } from './grade-comum';
import type { LinhaFicha } from './tipos';

/** O que o editor devolve a grade: o item escolhido na lista, ou o texto digitado (descricao). */
export type ValorComponente = { item: ItemResponse } | string;

interface RefEditorComponente {
  afterGuiAttached: () => void;
  getValue: () => ValorComponente;
}

/**
 * Editor da coluna "Componente" de uma linha de material (Sessao 37, bug: a coluna aceitava digitacao mas nao abria a lista para trocar de item).
 * Campo com busca no cadastro de itens (codigo ou descricao): digitar filtra, setas navegam, Enter ou clique escolhem. Escolher um item troca o
 * componente da linha (item, tipo, unidade). Texto sem escolher: numa linha COM item volta ao que era (o texto nao descola da descricao do item);
 * numa linha sem item vira a descricao, como antes. Esc cancela.
 */
export const EditorComponente = forwardRef<RefEditorComponente, CustomCellEditorProps<LinhaFicha>>(function EditorComponente(props, ref) {
  const lista = useId();
  const campo = useRef<HTMLInputElement>(null);
  const linha = props.data;
  const inicial = typeof props.value === 'string' ? props.value : '';
  const [texto, setTexto] = useState(inicial);
  const [tocou, setTocou] = useState(false);
  const [escolhido, setEscolhido] = useState<ItemResponse | null>(null);
  const [destaque, setDestaque] = useState(0);
  const termo = useDebounced(tocou ? texto.trim() : '', 250);
  const busca = useItens({ q: termo || undefined, ativo: true, limit: 20 });
  const excluir = ctx(props).itemIdDaFicha;
  const itens = (busca.data ?? []).filter((i) => i.id !== excluir);

  useImperativeHandle(ref, () => ({
    afterGuiAttached: () => {
      campo.current?.focus();
      campo.current?.select();
    },
    getValue: () => {
      if (escolhido) return { item: escolhido };
      // Linha com item: texto solto nao troca nada. Linha sem item: o texto e a descricao.
      return linha?.componenteItemId ? inicial : texto;
    },
  }));

  // A grade chama afterGuiAttached antes do React montar o campo (edicao aberta por codigo, ex.: "+ Linha"): o foco tambem vai no mount.
  useEffect(() => {
    campo.current?.focus();
    campo.current?.select();
  }, []);

  useEffect(() => {
    setDestaque(0);
  }, [termo]);

  function escolher(i: ItemResponse) {
    setEscolhido(i);
    props.onValueChange({ item: i });
    // o proximo ciclo ja leva o valor escolhido ao getValue
    setTimeout(() => props.stopEditing(), 0);
  }

  function aoTeclar(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setDestaque((d) => Math.min(d + 1, Math.max(itens.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setDestaque((d) => Math.max(d - 1, 0));
    } else if (e.key === 'Enter') {
      // A grade ignora Enter e setas nesta coluna (suppressKeyboardEvent): quem decide aqui e o editor.
      e.preventDefault();
      if (tocou && itens[destaque]) escolher(itens[destaque] as ItemResponse);
      else props.stopEditing();
    }
  }

  // A lista vai para o body (portal) logo abaixo do campo: a celula da grade corta tudo que passa da altura dela.
  const [posicao, setPosicao] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const r = campo.current?.getBoundingClientRect();
    if (r) setPosicao({ left: r.left, top: r.bottom + 2 });
  }, []);

  return (
    <div className="h-full w-full" data-testid="editor-componente">
      <input
        ref={campo}
        role="combobox"
        aria-expanded
        aria-controls={lista}
        aria-label="Componente (busque no cadastro de itens)"
        className="h-full w-full bg-card px-2 text-body outline-none"
        placeholder="Código ou descrição do item…"
        value={texto}
        onChange={(e) => {
          setTexto(e.target.value);
          setTocou(true);
          // Linha com item: o texto e so a busca (nao vira descricao). Linha sem item: o texto e a descricao.
          if (!linha?.componenteItemId) props.onValueChange(e.target.value);
        }}
        onKeyDown={aoTeclar}
      />
      {posicao &&
        createPortal(
          <ul
            id={lista}
            role="listbox"
            aria-label="Itens do cadastro"
            style={{ position: 'fixed', left: posicao.left, top: posicao.top }}
            className="z-[80] max-h-60 w-[420px] overflow-y-auto rounded-md border border-border bg-popover py-1 shadow-menu"
          >
            {!tocou && <li className="px-3 py-2 text-small text-ink-muted">Digite o código ou a descrição para trocar o componente.</li>}
            {tocou && busca.isError && (
              <li role="alert" className="px-3 py-2 text-small text-danger-ink">
                Não foi possível buscar itens: {busca.error?.message}
              </li>
            )}
            {tocou && !busca.isError && itens.length === 0 && (
              <li role="status" className="px-3 py-2 text-small text-ink-muted">
                {busca.isFetching ? 'Buscando…' : 'Nenhum item encontrado.'}
              </li>
            )}
            {tocou &&
              itens.map((i, n) => (
                <li key={i.id} role="option" aria-selected={n === destaque}>
                  <button
                    type="button"
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-small hover:bg-line-soft ${n === destaque ? 'bg-line-soft' : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => escolher(i)}
                  >
                    <span className="tabular rounded-xs bg-line-soft px-1.5 py-0.5 text-caption">{i.codigo}</span>
                    <span className="min-w-0 flex-1 truncate">{i.descricao}</span>
                  </button>
                </li>
              ))}
          </ul>,
          document.body,
        )}
    </div>
  );
});
